import { EXTERNAL_AUTH_TARGETS } from 'Contexts/AuthContext';
import { dateHelper } from 'utils/helper';
import { AppId } from 'types/shared/documents/restaurants';
import { SecurityPlusKeys } from 'CustomHooks/usePinAuth';
import firebase from 'utils/firebase';
import { websiteManagementPath } from 'App/Website/managedWebsiteApp';

export type ProductDefinition = {
  id: string;
  product: string;
  label: string;
  linkTo?: (rId: string, apps: AppId[]) => string;
  authorize?: SecurityPlusKeys;
  app?: AppId;
  isNew?: boolean;
  hideLabel?: string;
  dontShowOnHide?: boolean;
  promotionalLink?: (rId: string) => string;
  onClick?: (rId: string | null) => void | Promise<void>;
  adminOnly?: boolean;
  /** Not sold anymore: kept where active, not offered in "Hinzufügen" otherwise. */
  noLongerSold?: boolean;
  /** Shown only after a restaurant adds it via "Hinzufügen". */
  hiddenUntilAdded?: boolean;
  /** Shown only once Gastronaut activated a managed website (API, not apps); not offered before. */
  managedWebsite?: boolean;
  /**
   * Absent — no grey tile, not in Hinzufügen — until `app` is activated in the App
   * Store; its saved place and hidden choice are kept for a reactivation.
   */
  absentWithoutApp?: boolean;
};

/** A product as the current user sees it: `hide` when not available. */
export type EditorProduct = ProductDefinition & { locked: boolean; hide: boolean };

export const PRODUCTS: ProductDefinition[] = [
  {
    id: 'reservations',
    product: 'reservation',
    label: 'Reservations',
    linkTo: (rId: string) => `${rId}/reservations/${dateHelper()}/tablePlan`,
  },
  {
    id: 'ticketDoor',
    product: 'reservation',
    label: 'Ticket-Einlass',
    linkTo: (rId: string) => `/${rId}/ticketDoor`,
    app: 'ticket_door',
    absentWithoutApp: true,
    dontShowOnHide: true,
  },
  {
    id: 'light-reservations',
    product: 'light-reservation',
    label: 'Reservations',
    linkTo: (rId: string) =>
      `/${rId}/reservations/${dateHelper()}/reservationList`,
  },

  {
    id: 'calendar',
    product: 'light-reservation, reservation',
    label: 'Calendar',
    linkTo: (rId: string) => `/${rId}/calendar/${dateHelper()}`,
    authorize: 'dashboard.calendar.read',
  },
  {
    id: 'guestbook',
    product: 'light-reservation, reservation',
    label: 'Guest Book',
    linkTo: (rId: string, apps) =>
      apps.includes('guestbook' as any)
        ? `/${rId}/guestbookV02`
        : `/${rId}/guestbook`,
    authorize: 'dashboard.guestBook.read',
  },
  {
    id: 'guest-statistics',
    product: 'general',
    label: 'Guest Statistics',
    linkTo: (rId: string) => `/${rId}/statistics`,
    authorize: 'dashboard.statistics.read',
  },
  {
    id: 'delivery',
    product: 'delivery',
    label: 'Take Away',
    linkTo: (rId: string) => `/${rId}/takeAway/orders/${dateHelper()}`,
    authorize: 'dashboard.delivery.read',
    dontShowOnHide: true,
    noLongerSold: true,
  },
  {
    id: 'menu',
    product: 'menu',
    label: 'Digital Menu',
    app: 'digital_menu',
    onClick: async (rId: string | null) => {
      try {
        const user = firebase.auth().currentUser;

        if (!rId || !user) return;

        const idToken = await user.getIdToken(/* forceRefresh */ true);

        // Same "Login with Gastronaut" handoff as ?auth=menu-by-gastronaut,
        // plus the restaurant so the menu app opens its venue directly.
        window.location.href = `${EXTERNAL_AUTH_TARGETS['menu-by-gastronaut']}#token=${idToken}&restaurantId=${rId}`;
      } catch (error) {
        window.alert(error);
      }
    },
    authorize: 'dashboard.menu.read',
    // The old menu product does not say a restaurant uses Menu by Gastronaut.
    hiddenUntilAdded: true,
  },
  {
    id: 'feedback',
    product: 'feedback, followUp',
    label: 'Review Rocket',
    linkTo: (rId: string) => `${rId}/feedback`,
    authorize: 'dashboard.feedback.read',
  },
  {
    id: 'phone',
    product: 'general, phone',
    label: 'SIA',
    app: 'sia-phone-assistant',
    linkTo: (rId: string) => `/${rId}/sia-embed`,
    authorize: 'dashboard.sia.read',
    isNew: true,
    dontShowOnHide: true,
  },
  {
    id: 'addOns',
    product: 'reservation, addOns, addons',
    label: 'Add Ons',
    linkTo: (rId: string) => `/${rId}/experiences`,
    hideLabel: 'COMING SOON',
    // isNew: true,
    dontShowOnHide: true,
  },
  {
    id: 'vouchers',
    product: 'vouchers',
    label: 'Vouchers',
    // hideLabel: "COMING SOON",
    linkTo: (rId: string) => `/${rId}/vouchers`,
    authorize: 'dashboard.voucher.read',
    promotionalLink: (rId: string) =>
      `/${rId}/settings/general/apps?appId=voucher`,
    // dontShowOnHide: true,
  },
  {
    id: 'noShowProtection',
    product: 'light-reservation, reservation',
    label: 'No-Show Protection',
    app: 'noShowMollie',
    linkTo: (rId: string) => `/${rId}/no-show-protection`,
    promotionalLink: (rId: string) =>
      `/${rId}/settings/general/apps?appId=noShowMollie`,
    isNew: true,
  },
  {
    id: 'shop',
    product: 'general',
    label: 'Shop',
    app: 'shop',
    linkTo: (rId: string) => `/${rId}/shop/orders`,
    promotionalLink: (rId: string) => `/${rId}/settings/general/apps?appId=shop`,
    isNew: true,
  },
  {
    id: 'bookkeeping',
    product: 'general',
    label: 'Accounting',
    linkTo: (rId: string) => `/${rId}/bookkeeping/customer-invoices`,
    authorize: 'dashboard.bookkeeping.read',
    isNew: true,
    dontShowOnHide: true,
  },
  {
    id: 'coupons',
    product: 'reservation',
    app: 'coupon-management',
    label: 'Discount codes',
    linkTo: (rId: string) => `/${rId}/coupons`,
    adminOnly: true,
    isNew: true,
    dontShowOnHide: true,
  },
  {
    id: 'website',
    product: 'general, reservation, light-reservation',
    label: 'Website',
    linkTo: (rId: string) => websiteManagementPath(rId),
    adminOnly: true,
    managedWebsite: true,
    dontShowOnHide: true,
  },
  {
    id: 'marketing',
    product: 'general',
    app: 'marketing' as any,
    label: 'Marketing',
    linkTo: (rId: string) => `/${rId}/marketing`,
    isNew: true,
    dontShowOnHide: true,
  },
  {
    id: 'tickets',
    product: 'reservation',
    app: 'gastronaut-events' as any,
    label: 'Events',
    onClick: async (rId: string | null) => {
      try {
        const user = firebase.auth().currentUser;

        const GASTRONAUT_EVENTS_URL = 'http://localhost:5173';

        if (user) {
          const idToken = await user.getIdToken(/* forceRefresh */ true);
          // Redirect to App B with the token
          window.location.href = `${GASTRONAUT_EVENTS_URL}/${
            rId || ''
          }?token=${idToken}`;
        } else {
          // Handle the case where the user is not logged in
        }
      } catch (error) {
        window.alert(error);
      }
    },
    dontShowOnHide: true,
    noLongerSold: true,
  },
  {
    id: 'giftcards',
    product: 'vouchers',
    app: 'gastronaut-giftcards',
    label: 'Gift Cards',
    onClick: async (rId: string | null) => {
      try {
        const user = firebase.auth().currentUser;

        const GASTRONAUT_GIFTCARD_URL = 'http://localhost:5173';

        if (user) {
          const idToken = await user.getIdToken(/* forceRefresh */ true);
          // Redirect to App B with the token
          window.location.href = `${GASTRONAUT_GIFTCARD_URL}/${
            rId || ''
          }?token=${idToken}`;
        } else {
          // Handle the case where the user is not logged in
        }
      } catch (error) {
        window.alert(error);
      }
    },
    dontShowOnHide: true,
    noLongerSold: true,
  },
  // {
  //   id: 'phone',
  //   product: 'general',
  //   app: 'sia-phone-assistant',
  //   label: 'Sia 2.0',
  //   onClick: async (rId: string | null) => {
  //     try {
  //       const user = firebase.auth().currentUser;

  //       const SIA_PHONE_ASSISTANT_URL = 'https://sia-app.gastronaut.ai';

  //       if (user) {
  //         const idToken = await user.getIdToken(/* forceRefresh */ true);
  //         // Redirect to App B with the token
  //         window.location.href = `${SIA_PHONE_ASSISTANT_URL}/login?token=${idToken}&restaurantId=${rId}`;
  //       } else {
  //         // Handle the case where the user is not logged in
  //       }
  //     } catch (error) {
  //       window.alert(error);
  //     }
  //   },
  //   isNew: true,
  //   dontShowOnHide: true,
  // },
  // {
  //   id: "order",
  //   product: "order",
  //   label: "Guest Order",
  //   linkTo: (rId: string) => `${rId}/order`,
  //   hideLabel: "COMING SOON",
  // },
];

/** Support is part of the restaurant layout; its action needs the restaurant context. */
export const SUPPORT_ID = 'support';

export const PRODUCT_IDS = [...PRODUCTS.map((product) => product.id), SUPPORT_ID];

/** Their availability arrives later from the API; their saved place is kept meanwhile. */
export const MANAGED_WEBSITE_IDS = PRODUCTS.filter(
  (product) => product.managedWebsite,
).map((product) => product.id);

/** Absent while their app is off; their saved place is kept meanwhile. */
export const APP_GATED_IDS = PRODUCTS.filter(
  (product) => product.absentWithoutApp,
).map((product) => product.id);
